"use client";

import { CSSProperties } from "react";
import { GripVertical, Trash2 } from "lucide-react";
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    ChecklistAppearance,
    ChecklistBlock as ChecklistBlockType,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useResolvedGridGap } from "../../../hooks/useGridGap";
import { resolveChecklistAppearance } from "../../../lib/blockRegistry";
import RichText from "../text/richText";
import { IndexBadge } from "../shared/IndexBadge";
import { INDEX_ICON } from "../shared/indexIcon";
import RichHtml from "./richHtml";

const cardStyle = (a: ChecklistAppearance): CSSProperties => ({
    backgroundColor: a.cardBackground,
    borderColor: a.border,
    borderWidth: a.borderWidth,
    borderStyle: "solid",
});

export default function ChecklistBlock({
    block,
}: {
    block: ChecklistBlockType;
}) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const editable = useBlockEditable();
    const gridGap = useResolvedGridGap(block.id);
    const { items } = block.data;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveChecklistAppearance(block.data.appearance, theme);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
    );

    const update = (patch: Partial<ChecklistBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const patchItem = (id: string, text: string) =>
        update({
            items: items.map((it) => (it.id === id ? { ...it, text } : it)),
        });
    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return;
        const from = items.findIndex((it) => it.id === active.id);
        const to = items.findIndex((it) => it.id === over.id);
        if (from === -1 || to === -1) return;
        update({ items: arrayMove(items, from, to) });
    };

    if (!editable) {
        const filled = items.filter((it) => it.text);
        if (filled.length === 0) return null;
        // A plain numbered list: ticking items off read as a task tracker the
        // lesson never used, so there is no progress counter and no click.
        return (
            <div className="lomda-v3-card-surface flex flex-col gap-4 rounded-2xl border p-6 shadow-sm">
                <div className="flex flex-col" style={{ gap: gridGap }}>
                    {filled.map((it, i) => (
                        <div
                            key={it.id}
                            className="lomda-v3-card-text flex w-full items-center gap-4 rounded-2xl p-4 text-start"
                            style={cardStyle(appearance)}
                        >
                            <IndexBadge size={INDEX_ICON.badgeSize}>
                                {i + 1}
                            </IndexBadge>
                            <RichHtml
                                html={it.text}
                                className="lomda-v3-text-p"
                            />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
        >
            <SortableContext
                items={items.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
            >
                <div className="flex flex-col" style={{ gap: gridGap }}>
                    {items.map((it, i) => (
                        <SortableChecklistItem
                            key={it.id}
                            id={it.id}
                            index={i}
                            appearance={appearance}
                            blockId={block.id}
                            initialHtml={it.text}
                            onCommit={(text) => patchItem(it.id, text)}
                            onDelete={() =>
                                update({
                                    items: items.filter((x) => x.id !== it.id),
                                })
                            }
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}

function SortableChecklistItem({
    id,
    index,
    appearance,
    blockId,
    initialHtml,
    onCommit,
    onDelete,
}: {
    id: string;
    index: number;
    appearance: ChecklistAppearance;
    blockId: string;
    initialHtml: string;
    onCommit: (text: string) => void;
    onDelete: () => void;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    return (
        <div
            ref={setNodeRef}
            className="lomda-v3-card-text flex items-center gap-3 rounded-2xl p-4"
            style={{
                ...cardStyle(appearance),
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.4 : 1,
            }}
        >
            <span
                {...attributes}
                {...listeners}
                className="shrink-0 cursor-grab touch-none text-muted-foreground/50"
            >
                <GripVertical className="size-4" />
            </span>
            <IndexBadge size={INDEX_ICON.badgeSize}>{index + 1}</IndexBadge>
            <div className="min-w-0 flex-1">
                <RichText
                    initialHtml={initialHtml}
                    editable
                    useGlobalToolbar
                    blockId={blockId}
                    groupKey={`${blockId}:checklist-item`}
                    placeholder={t("lomda-v3-editor.checklist.item-placeholder")}
                    onCommit={onCommit}
                />
            </div>
            <button
                type="button"
                aria-label={t("lomda-v3-editor.block.delete")}
                onClick={onDelete}
                className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-destructive"
            >
                <Trash2 className="size-4" />
            </button>
        </div>
    );
}
